import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { superchatAPI } from '@/lib/api';
import InstagramAnbindung from '@/components/InstagramAnbindung';
import { toast } from 'sonner';
import {
  Bot, BookOpen, Settings2, BarChart3, HeartPulse, Plus, Pencil, Trash2,
  Loader2, RefreshCw, Plug, ListOrdered, Droplets,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/* /agent — Verwaltung des Superchat-KI-Agenten (Modul C):
   Wissensbasis (einzige Faktenquelle), Konfiguration je Mandant (Modus-
   Stufenleiter aus -> schatten -> nacht -> live), die vier Freigabe-
   Kennzahlen aus dem Bauplan und der Health-Status. */

const MANDANTEN = ['p1', 'ptl'];
const MODI = [
  { wert: 'aus', label: 'Aus', hinweis: 'Kein Modellaufruf' },
  { wert: 'schatten', label: 'Schatten', hinweis: 'Nur Vorschläge, nichts geht raus' },
  { wert: 'nacht', label: 'Nacht', hinweis: 'Sendet nur außerhalb der Öffnungszeiten' },
  { wert: 'live', label: 'Live', hinweis: 'Sendet automatisch (validierte Antworten)' },
];

const LEERER_EINTRAG = {
  mandant: 'p1', kategorie: 'allgemein', frage: '', antwort: '', tags: [],
  aktiv: true, gueltig_bis: null,
};

const LEERER_WL = {
  mandant: 'zinzino', vorname: '', nachname: '', telefon: '', email: '',
  sportart: '', zielgruppe: '', wunschzeiten: '', prioritaet: 0, notiz: '',
};

/* Warteliste (Modul B): Wer hier steht, bekommt automatisch ein
   WhatsApp-Angebot, sobald ein passender Slot frei wird — mehr als 24 h
   Vorlauf: exklusiv 2 h reserviert; kurzfristiger: parallel an bis zu 5,
   der Schnellste gewinnt. Wunschzeiten als "Mo 16:00-19:00, Fr 10:00-12:00". */
function Warteliste() {
  const [eintraege, setEintraege] = useState([]);
  const [mandant, setMandant] = useState('zinzino');
  const [neu, setNeu] = useState(null);
  const [speichert, setSpeichert] = useState(false);

  const laden = useCallback(async () => {
    try { setEintraege((await superchatAPI.warteliste({ mandant })).data); }
    catch { toast.error('Warteliste konnte nicht geladen werden'); }
  }, [mandant]);
  useEffect(() => { laden(); }, [laden]);

  const wunschzeitenParsen = (text) =>
    text.split(',').map((teil) => {
      const m = teil.trim().match(/^(Mo|Di|Mi|Do|Fr|Sa|So)(?:\s+(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2}))?$/i);
      if (!m) return null;
      const z = { tag: m[1][0].toUpperCase() + m[1][1].toLowerCase() };
      if (m[2]) { z.von = m[2].padStart(5, '0'); z.bis = m[3].padStart(5, '0'); }
      return z;
    }).filter(Boolean);

  const speichern = async () => {
    setSpeichert(true);
    try {
      await superchatAPI.wartelisteAnlegen({
        ...neu,
        prioritaet: Number(neu.prioritaet) || 0,
        wunschzeiten: wunschzeitenParsen(neu.wunschzeiten || ''),
      });
      toast.success('Auf die Warteliste gesetzt');
      setNeu(null); laden();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
    setSpeichert(false);
  };

  const entfernen = async (id) => {
    try { await superchatAPI.wartelisteEntfernen(id); laden(); }
    catch { toast.error('Entfernen fehlgeschlagen'); }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <select value={mandant} onChange={(e) => setMandant(e.target.value)}
                className="text-sm rounded-md border border-border bg-background px-2 py-1.5">
          {['zinzino', 'ptl', 'p1'].map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
        </select>
        <Button size="sm" className="gap-1.5" onClick={() => setNeu({ ...LEERER_WL, mandant })}>
          <Plus className="h-4 w-4" /> Kandidat
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Wird ein Termin abgesagt, bekommen passende Kandidaten automatisch ein WhatsApp-Angebot
        (über 24 h Vorlauf: exklusiv, 2 h reserviert · darunter: parallel an bis zu 5, wer zuerst
        zusagt). Nach drei erfolglosen Runden entsteht eine Aufgabe.
      </p>
      <div className="space-y-2">
        {eintraege.length === 0 && (
          <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">
            Warteliste für {mandant.toUpperCase()} ist leer.
          </CardContent></Card>
        )}
        {eintraege.map((e) => (
          <Card key={e.id}>
            <CardContent className="py-3 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm">
                  {e.vorname} {e.nachname}
                  {e.prioritaet > 0 && <Badge variant="outline" className="ml-2 text-[10px]">Prio {e.prioritaet}</Badge>}
                </p>
                <p className="text-xs text-muted-foreground">
                  {e.telefon}
                  {e.sportart && ` · ${e.sportart}`}
                  {(e.wunschzeiten || []).length > 0 &&
                    ` · ${e.wunschzeiten.map((w) => w.von ? `${w.tag} ${w.von}–${w.bis}` : w.tag).join(', ')}`}
                  {e.notiz && ` · ${e.notiz}`}
                </p>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7 flex-none" onClick={() => entfernen(e.id)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!neu} onOpenChange={(o) => !o && setNeu(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Auf die Warteliste</DialogTitle></DialogHeader>
          {neu && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <select value={neu.mandant} onChange={(e) => setNeu({ ...neu, mandant: e.target.value })}
                        className="text-sm rounded-md border border-border bg-background px-2 py-1.5">
                  {['zinzino', 'ptl', 'p1'].map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
                </select>
                <Input placeholder="Priorität (0–10)" type="number" min={0} max={10} value={neu.prioritaet}
                       onChange={(e) => setNeu({ ...neu, prioritaet: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Vorname" value={neu.vorname}
                       onChange={(e) => setNeu({ ...neu, vorname: e.target.value })} />
                <Input placeholder="Nachname" value={neu.nachname}
                       onChange={(e) => setNeu({ ...neu, nachname: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Telefon (+49 …)" value={neu.telefon}
                       onChange={(e) => setNeu({ ...neu, telefon: e.target.value })} />
                <Input placeholder="E-Mail (optional)" value={neu.email}
                       onChange={(e) => setNeu({ ...neu, email: e.target.value })} />
              </div>
              <Input placeholder='Wunschzeiten, z. B. "Mo 16:00-19:00, Fr" (leer = jederzeit)'
                     value={neu.wunschzeiten}
                     onChange={(e) => setNeu({ ...neu, wunschzeiten: e.target.value })} />
              <Input placeholder="Notiz (optional)" value={neu.notiz}
                     onChange={(e) => setNeu({ ...neu, notiz: e.target.value })} />
              <Button onClick={speichern}
                      disabled={speichert || neu.vorname.trim().length < 2 || neu.telefon.trim().length < 5}>
                {speichert ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Auf die Warteliste'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Prozent({ wert }) {
  if (wert === null || wert === undefined) return <span className="text-muted-foreground">—</span>;
  return <span>{Math.round(wert * 100)} %</span>;
}

export default function AgentPage() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(() => (params.get('tab') === 'anbindung' ? 'anbindung' : 'wissensbasis'));

  // Rückkehr vom Instagram-Login (/api/instagram/rueckruf): Ergebnis einmal melden, dann die Adresse aufräumen
  useEffect(() => {
    if (params.get('instagram') === 'verbunden') {
      toast.success(params.get('instagram_abo') === 'fehlt' ? 'Instagram verbunden, aber das Nachrichten-Abo fehlt noch' : 'Instagram ist verbunden');
    } else if (params.get('instagram_fehler')) {
      toast.error(`Instagram nicht verbunden: ${params.get('instagram_fehler')}`);
    } else return;
    setParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Wissensbasis
  const [eintraege, setEintraege] = useState([]);
  const [wbMandant, setWbMandant] = useState('p1');
  const [editor, setEditor] = useState(null);        // Eintrag im Dialog
  const [speichert, setSpeichert] = useState(false);

  // Config
  const [cfgMandant, setCfgMandant] = useState('p1');
  const [cfg, setCfg] = useState(null);

  // Auswertung + Health
  const [auswertung, setAuswertung] = useState(null);
  const [health, setHealth] = useState(null);

  // Meta-Anbindung
  const [meta, setMeta] = useState(null);

  // WhatsApp-Bruecke (verknuepftes Geraet)
  const [bruecke, setBruecke] = useState(null);

  const ladeWissensbasis = useCallback(async () => {
    try { setEintraege((await superchatAPI.wissensbasis({ mandant: wbMandant })).data); }
    catch { toast.error('Wissensbasis konnte nicht geladen werden'); }
  }, [wbMandant]);

  const ladeConfig = useCallback(async () => {
    try { setCfg((await superchatAPI.agentConfig(cfgMandant)).data); }
    catch { toast.error('Konfiguration konnte nicht geladen werden'); }
  }, [cfgMandant]);

  const ladeAuswertung = useCallback(async () => {
    try {
      const [a, h] = await Promise.all([superchatAPI.auswertung({ tage: 14 }), superchatAPI.health()]);
      setAuswertung(a.data); setHealth(h.data);
    } catch { toast.error('Auswertung konnte nicht geladen werden'); }
  }, []);

  const ladeMeta = useCallback(async () => {
    try { setMeta((await superchatAPI.metaConfig()).data); } catch { /* leer ok */ }
  }, []);

  const ladeBruecke = useCallback(async () => {
    try { setBruecke((await superchatAPI.brueckeZustand()).data); } catch { /* leer ok */ }
  }, []);

  useEffect(() => { ladeWissensbasis(); }, [ladeWissensbasis]);
  useEffect(() => { ladeConfig(); }, [ladeConfig]);
  useEffect(() => { ladeAuswertung(); ladeMeta(); ladeBruecke(); }, [ladeAuswertung, ladeMeta, ladeBruecke]);

  // Solange der QR-Code sichtbar sein muss, im Takt nachfragen — er wechselt
  // etwa jede Minute, und nach dem Scannen soll die Karte von selbst umspringen.
  useEffect(() => {
    if (tab !== 'anbindung') return undefined;
    const wartetAufKopplung = bruecke?.live?.erreichbar && !bruecke?.live?.verbunden;
    const takt = setInterval(ladeBruecke, wartetAufKopplung ? 4000 : 20000);
    return () => clearInterval(takt);
  }, [tab, bruecke, ladeBruecke]);

  const eintragSpeichern = async () => {
    setSpeichert(true);
    try {
      const daten = {
        ...editor,
        tags: Array.isArray(editor.tags)
          ? editor.tags
          : editor.tags.split(',').map((t) => t.trim()).filter(Boolean),
      };
      if (editor.id) await superchatAPI.wissensbasisAendern(editor.id, daten);
      else await superchatAPI.wissensbasisAnlegen(daten);
      toast.success('Gespeichert');
      setEditor(null);
      ladeWissensbasis();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
    setSpeichert(false);
  };

  const eintragLoeschen = async (id) => {
    try { await superchatAPI.wissensbasisLoeschen(id); ladeWissensbasis(); toast.success('Gelöscht'); }
    catch { toast.error('Löschen fehlgeschlagen'); }
  };

  const configSpeichern = async (aenderung) => {
    try {
      const neu = { ...cfg, ...aenderung };
      const { mandant, geaendert_am, ...body } = neu;
      setCfg((await superchatAPI.agentConfigSchreiben(cfgMandant, body)).data);
      toast.success('Konfiguration gespeichert');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
      ladeConfig();
    }
  };

  const brueckeSchalten = async (aktiv) => {
    try {
      await superchatAPI.brueckeKonfig({ aktiv });
      toast.success(aktiv ? 'WhatsApp läuft jetzt über die Brücke' : 'Brücke ausgeschaltet');
      ladeBruecke();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Umschalten fehlgeschlagen'); }
  };

  const brueckeNeuKoppeln = async () => {
    if (!window.confirm('Kopplung verwerfen? Bis der neue QR-Code gescannt ist, '
                        + 'geht über die Brücke keine Nachricht mehr raus.')) return;
    try {
      await superchatAPI.brueckeNeuKoppeln();
      toast.success('Kopplung verworfen — neuer QR-Code kommt gleich');
      setTimeout(ladeBruecke, 2000);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Neu koppeln fehlgeschlagen'); }
  };

  const metaSpeichern = async () => {
    try {
      await superchatAPI.metaConfigSchreiben(meta);
      toast.success('Meta-Anbindung gespeichert');
      ladeMeta();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="agent-page">
      <div className="flex items-center gap-2">
        <Bot className="h-5 w-5" />
        <h1 className="text-xl font-bold">KI-Agent</h1>
        {cfg && <Badge variant={cfg.modus === 'aus' ? 'secondary' : 'default'}>{cfgMandant.toUpperCase()}: {cfg.modus}</Badge>}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="wissensbasis" className="gap-1.5"><BookOpen className="h-3.5 w-3.5" /> Wissensbasis</TabsTrigger>
          <TabsTrigger value="config" className="gap-1.5"><Settings2 className="h-3.5 w-3.5" /> Konfiguration</TabsTrigger>
          <TabsTrigger value="auswertung" className="gap-1.5"><BarChart3 className="h-3.5 w-3.5" /> Auswertung</TabsTrigger>
          <TabsTrigger value="warteliste" className="gap-1.5"><ListOrdered className="h-3.5 w-3.5" /> Warteliste</TabsTrigger>
          <TabsTrigger value="anbindung" className="gap-1.5"><Plug className="h-3.5 w-3.5" /> Anbindung</TabsTrigger>
        </TabsList>

        {/* ───────────── Warteliste (Modul B) ───────────── */}
        <TabsContent value="warteliste" className="space-y-3">
          <Warteliste />
        </TabsContent>

        {/* ───────────── Wissensbasis ───────────── */}
        <TabsContent value="wissensbasis" className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <select value={wbMandant} onChange={(e) => setWbMandant(e.target.value)}
                    className="text-sm rounded-md border border-border bg-background px-2 py-1.5">
              {MANDANTEN.map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
            </select>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="gap-1.5"
                      onClick={async () => {
                        try {
                          const r = await superchatAPI.wissensbasisFbcLaden(wbMandant);
                          toast.success(`FBC-Methodik: ${r.data.angelegt} neu, ${r.data.uebersprungen} schon vorhanden`);
                          ladeWissensbasis();
                        } catch { toast.error('Laden fehlgeschlagen'); }
                      }}>
                <BookOpen className="h-4 w-4" /> FBC-Methodik laden
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5"
                      onClick={async () => {
                        try {
                          const r = await superchatAPI.wissensbasisZinzinoLaden(wbMandant);
                          toast.success(`Zinzino-Fakten: ${r.data.angelegt} neu, ${r.data.uebersprungen} schon vorhanden`);
                          ladeWissensbasis();
                        } catch { toast.error('Laden fehlgeschlagen'); }
                      }}>
                <Droplets className="h-4 w-4" /> Zinzino-Fakten laden
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5"
                      title="Vergütungsplan, Ziele und Storybook-Regeln für Philip & Elvan — Kategorie 'intern', sieht der Kunden-Agent nie"
                      onClick={async () => {
                        try {
                          const r = await superchatAPI.wissensbasisZinzinoInternLaden(wbMandant);
                          toast.success(`Geschäftswissen (intern): ${r.data.angelegt} neu, ${r.data.uebersprungen} schon vorhanden`);
                          ladeWissensbasis();
                        } catch { toast.error('Laden fehlgeschlagen'); }
                      }}>
                <BookOpen className="h-4 w-4" /> Geschäftswissen (intern)
              </Button>
              <Button size="sm" className="gap-1.5"
                      onClick={() => setEditor({ ...LEERER_EINTRAG, mandant: wbMandant })}
                      data-testid="wb-neu">
                <Plus className="h-4 w-4" /> Eintrag
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Die Wissensbasis ist die einzige Faktenquelle des Agenten — was hier nicht steht,
            beantwortet er nicht. Empfohlen zum Start: Kurszeiten je Sportart und Alter,
            Einstiegsalter, Ablauf Probetraining, Mitbringen, Adresse und Parken, Ferien, Ansprechpartner.
          </p>
          <div className="space-y-2">
            {eintraege.length === 0 && (
              <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">
                Noch keine Einträge für {wbMandant.toUpperCase()}.
              </CardContent></Card>
            )}
            {eintraege.map((e) => (
              <Card key={e.id} className={e.aktiv ? '' : 'opacity-50'}>
                <CardContent className="py-3 flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">{e.kategorie}</Badge>
                      {!e.aktiv && <Badge variant="secondary" className="text-[10px]">inaktiv</Badge>}
                      {(e.tags || []).map((t) => <span key={t} className="text-[10px] text-muted-foreground">#{t}</span>)}
                    </div>
                    <p className="font-medium text-sm mt-1">{e.frage}</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{e.antwort}</p>
                  </div>
                  <div className="flex gap-1 flex-none">
                    <Button variant="ghost" size="icon" className="h-7 w-7"
                            onClick={() => setEditor({ ...e, tags: (e.tags || []).join(', ') })}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => eintragLoeschen(e.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* ───────────── Konfiguration ───────────── */}
        <TabsContent value="config" className="space-y-3">
          <select value={cfgMandant} onChange={(e) => setCfgMandant(e.target.value)}
                  className="text-sm rounded-md border border-border bg-background px-2 py-1.5">
            {MANDANTEN.map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
          </select>
          {!cfg ? <Loader2 className="h-5 w-5 animate-spin" /> : (
            <div className="grid gap-3 md:grid-cols-2">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Modus</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {MODI.map((m) => (
                    <label key={m.wert} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="radio" name="modus" checked={cfg.modus === m.wert}
                             onChange={() => configSpeichern({ modus: m.wert })} />
                      <span className="font-medium">{m.label}</span>
                      <span className="text-xs text-muted-foreground">{m.hinweis}</span>
                    </label>
                  ))}
                  <p className="text-xs text-muted-foreground pt-1">
                    Der Modus lässt sich nur stufenweise erhöhen. Freigabe für „Nacht" laut Bauplan erst,
                    wenn zwei Wochen lang keine Fehleskalation auftrat und über 60 % der Vorschläge
                    unverändert freigegeben wurden.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Regeln</CardTitle></CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!cfg.preisfreigabe}
                           onChange={(e) => configSpeichern({ preisfreigabe: e.target.checked })} />
                    Preisnennung erlaubt (nur wörtlich aus der Wissensbasis)
                  </label>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Eskalations-E-Mail</p>
                    <Input value={cfg.eskalations_email || ''} placeholder="team@p1-pforzheim.de"
                           onChange={(e) => setCfg({ ...cfg, eskalations_email: e.target.value })}
                           onBlur={() => configSpeichern({})} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Modell</p>
                    <select value={cfg.modell}
                            onChange={(e) => configSpeichern({ modell: e.target.value })}
                            className="w-full text-sm rounded-md border border-border bg-background px-2 py-1.5">
                      {['claude-sonnet-4.5', 'gpt-5.2', 'gpt-4o', 'gemini-2.5-flash'].map((m) =>
                        <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Öffnungszeiten (für „Nacht" und Antwortzusagen)</p>
                    {(cfg.oeffnungszeiten || []).map((z, i) => (
                      <p key={i} className="text-xs">{z.tag} {z.von}–{z.bis}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* ───────────── Auswertung ───────────── */}
        <TabsContent value="auswertung" className="space-y-3">
          <div className="flex justify-end">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={ladeAuswertung}>
              <RefreshCw className="h-3.5 w-3.5" /> Aktualisieren
            </Button>
          </div>
          {auswertung && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Card><CardContent className="py-4">
                <p className="text-2xl font-bold"><Prozent wert={auswertung.anteil_sicherheit_hoch} /></p>
                <p className="text-xs text-muted-foreground">Sicherheit „hoch" ({auswertung.laeufe_gesamt} Läufe / {auswertung.zeitraum_tage} Tage)</p>
              </CardContent></Card>
              <Card><CardContent className="py-4">
                <p className="text-2xl font-bold"><Prozent wert={auswertung.anteil_unveraendert_freigegeben} /></p>
                <p className="text-xs text-muted-foreground">Unverändert freigegeben (Ziel: über 60 %)</p>
              </CardContent></Card>
              <Card><CardContent className="py-4">
                <p className="text-2xl font-bold"><Prozent wert={auswertung.anteil_bearbeitet} /></p>
                <p className="text-xs text-muted-foreground">Vor dem Senden bearbeitet</p>
              </CardContent></Card>
              <Card><CardContent className="py-4">
                <p className={`text-2xl font-bold ${auswertung.fehleskalationen > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {auswertung.fehleskalationen}
                </p>
                <p className="text-xs text-muted-foreground">Fehleskalationen (Ziel: 0 über zwei Wochen)</p>
              </CardContent></Card>
            </div>
          )}
          {health && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1.5">
                <HeartPulse className="h-4 w-4" /> Health
              </CardTitle></CardHeader>
              <CardContent className="grid gap-2 sm:grid-cols-2 text-sm">
                <p>Meta-Anbindung: {health.meta_konfiguriert
                  ? <Badge className="ml-1">konfiguriert</Badge>
                  : <Badge variant="secondary" className="ml-1">fehlt</Badge>}</p>
                <p>Letzter Webhook-Empfang: <span className="text-muted-foreground">{health.letzter_webhook_empfang || 'nie'}</span></p>
                <p>Letzter erfolgreicher Send: <span className="text-muted-foreground">{health.letzter_erfolgreicher_send || 'nie'}</span></p>
                <p>Offene Vorschläge: {health.offene_vorschlaege}</p>
                <p>Offene Terminangebote: {health.offene_terminangebote}</p>
                <p>Fehler (24 h): {health.fehler_24h + health.agent_fehler_24h}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ───────────── Meta-Anbindung ───────────── */}
        <TabsContent value="anbindung" className="space-y-3">

          {/* WhatsApp-Bruecke: verknuepftes Geraet an der Handynummer.
              Steht oben, weil das der Weg ist, der im Alltag laeuft. */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2">
              WhatsApp-Brücke (verknüpftes Gerät)
              {bruecke?.live?.verbunden
                ? <Badge>verbunden{bruecke.live.nummer ? ` · ${bruecke.live.nummer}` : ''}</Badge>
                : <Badge variant="secondary">nicht verbunden</Badge>}
              {bruecke?.gespeichert?.aktiv
                ? <Badge>Versand läuft hierüber</Badge>
                : <Badge variant="outline">aus</Badge>}
            </CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p className="text-xs text-muted-foreground">
                Die Brücke meldet sich wie WhatsApp Web als weiteres Gerät an
                der Studio-Nummer an. Das Handy bleibt das Hauptgerät und läuft
                unverändert weiter. Eingehende Nachrichten landen im Posteingang,
                Antworten vom Handy stehen ebenfalls im Verlauf.
              </p>

              {!bruecke && <p className="text-xs text-muted-foreground">Wird geladen …</p>}

              {bruecke && !bruecke.live?.eingerichtet && (
                <p className="text-xs text-amber-600">
                  {bruecke.live?.fehler || 'Der Dienst ist noch nicht eingerichtet.'}
                </p>
              )}

              {bruecke?.live?.eingerichtet && !bruecke.live.erreichbar && (
                <p className="text-xs text-amber-600">
                  Der Brücken-Dienst antwortet nicht: {bruecke.live.fehler || 'unbekannter Grund'}
                </p>
              )}

              {bruecke?.live?.qr && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Am Handy: WhatsApp → Einstellungen → <strong>Verknüpfte Geräte</strong> →
                    Gerät hinzufügen. Dann diesen Code scannen. Er wechselt etwa
                    jede Minute von selbst.
                  </p>
                  <img src={bruecke.live.qr} alt="QR-Code zum Koppeln"
                       className="w-56 h-56 border rounded bg-white p-2" />
                </div>
              )}

              {bruecke?.live?.verlauf && (bruecke.live.verlauf.laeuft || bruecke.live.verlauf.nachrichten > 0) && (
                <p className="text-xs text-muted-foreground">
                  Verlauf: {bruecke.live.verlauf.nachrichten} Nachrichten aus {bruecke.live.verlauf.chats} Chats
                  {bruecke.live.verlauf.laeuft ? ' — wird noch übernommen …' : ' übernommen'}
                  {bruecke.live.verlauf.fehler ? ` · ${bruecke.live.verlauf.fehler} nicht angekommen` : ''}
                </p>
              )}

              {bruecke?.live?.verbunden && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Versand in der letzten Stunde: {bruecke.live.versand_letzte_stunde ?? 0} von
                    höchstens {bruecke.live.max_pro_stunde ?? '—'}
                    {bruecke.live.warteschlange
                      ? ` · ${bruecke.live.warteschlange} in der Warteschlange` : ''}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {bruecke.gespeichert?.aktiv ? (
                      <Button variant="outline" onClick={() => brueckeSchalten(false)}>
                        Versand zurück auf die Cloud API
                      </Button>
                    ) : (
                      <Button onClick={() => brueckeSchalten(true)}>
                        Versand auf die Brücke umstellen
                      </Button>
                    )}
                    <Button variant="outline" onClick={brueckeNeuKoppeln}>Neu koppeln</Button>
                  </div>
                </div>
              )}

              {bruecke?.live?.erreichbar && !bruecke.live.verbunden && !bruecke.live.qr && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Der Dienst läuft, hat aber keine Verbindung und zeigt keinen Code —
                    meist eine hinfällige Kopplung. „Neu koppeln" verwirft sie und
                    holt einen frischen QR-Code.
                  </p>
                  <Button variant="outline" onClick={brueckeNeuKoppeln}>Neu koppeln</Button>
                </div>
              )}

              {bruecke?.live?.protokoll?.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">Letzte Meldungen des Dienstes</summary>
                  <ul className="mt-1 space-y-0.5 font-mono">
                    {bruecke.live.protokoll.map((z, i) => (
                      <li key={i} className={z.stufe >= 50 ? 'text-red-600' : z.stufe >= 40 ? 'text-amber-600' : 'text-muted-foreground'}>
                        {new Date(z.zeit).toLocaleTimeString('de-DE')} {z.text}{z.fehler ? ` — ${z.fehler}` : ''}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {bruecke?.gespeichert?.zuletzt_gesehen && (
                <p className="text-xs text-muted-foreground">
                  Zuletzt gemeldet: {new Date(bruecke.gespeichert.zuletzt_gesehen).toLocaleString('de-DE')}
                  {bruecke.gespeichert.fehler ? ` · ${bruecke.gespeichert.fehler}` : ''}
                </p>
              )}
            </CardContent>
          </Card>

          <InstagramAnbindung />

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Meta WhatsApp Cloud API</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p className="text-xs text-muted-foreground">
                Webhook-URL für die Meta-App: <code className="bg-muted px-1 rounded">https://crm.p1-pforzheim.de/api/webhook/whatsapp</code>.
                Wichtig (Bauplan, Abschnitt 1): Am Cut-Tag darf nur noch das CRM am Webhook hängen —
                n8n und OneClickGym werden an einem Tag abgehängt, nicht schleichend.
              </p>
              {meta && (
                <>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!meta.enabled}
                           onChange={(e) => setMeta({ ...meta, enabled: e.target.checked })} />
                    Aktiv
                  </label>
                  {[['verify_token', 'Verify-Token (frei wählbar, in Meta eintragen)'],
                    ['app_secret', 'App-Secret'],
                    ['access_token', 'Access-Token (System-User)'],
                    ['app_id', 'App-ID (Meta-App, für das Verbinden der Nummer)'],
                    ['es_config_id', 'Konfigurations-ID des Anmeldeablaufs (Embedded Signup)']]
                    .map(([feld, label]) => (
                    <div key={feld}>
                      <p className="text-xs text-muted-foreground mb-1">{label}</p>
                      <Input type={['verify_token', 'app_id', 'es_config_id'].includes(feld) ? 'text' : 'password'}
                             value={meta[feld] || ''}
                             onChange={(e) => setMeta({ ...meta, [feld]: e.target.value })} />
                    </div>
                  ))}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      Nummern (phone_number_id → Mandant, eine Zeile je Nummer als „id=p1")
                    </p>
                    <textarea rows={2} className="w-full text-sm rounded-md border border-border bg-background p-2"
                              value={Object.entries(meta.nummern || {}).map(([k, v]) => `${k}=${v}`).join('\n')}
                              onChange={(e) => {
                                const nummern = {};
                                e.target.value.split('\n').forEach((zeile) => {
                                  const [k, v] = zeile.split('=').map((s) => s.trim());
                                  if (k && v) nummern[k] = v;
                                });
                                setMeta({ ...meta, nummern });
                              }} />
                  </div>
                  <Button onClick={metaSpeichern}>Speichern</Button>

                  {/* Coexistence: Das Verbinden der eigenen Nummer laeuft ueber
                      Metas Anmeldebaustein und liegt deshalb auf einer eigenen
                      Seite ausserhalb dieser App — sie braucht eine weitere
                      Sicherheitsregel, die hier nicht gelten soll. */}
                  <div className="border-t pt-3 mt-1">
                    <p className="text-xs text-muted-foreground mb-2">
                      Eigene WhatsApp-Nummer an die Cloud API hängen (Coexistence —
                      das Handy bleibt parallel nutzbar). Setzt voraus, dass die
                      Meta-App den Status <strong>Tech-Anbieter</strong> hat und
                      App-ID, Konfigurations-ID und App-Secret oben gespeichert sind.
                    </p>
                    <Button variant="outline" asChild>
                      <a href="/wa-anmeldung">Nummer verbinden …</a>
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Wissensbasis-Editor */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editor?.id ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <select value={editor.mandant} onChange={(e) => setEditor({ ...editor, mandant: e.target.value })}
                        className="text-sm rounded-md border border-border bg-background px-2 py-1.5">
                  {MANDANTEN.map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
                </select>
                <Input placeholder="Kategorie (z. B. kurszeiten)" value={editor.kategorie}
                       onChange={(e) => setEditor({ ...editor, kategorie: e.target.value })} />
              </div>
              <Input placeholder="Frage" value={editor.frage}
                     onChange={(e) => setEditor({ ...editor, frage: e.target.value })} data-testid="wb-frage" />
              <textarea placeholder="Antwort (wörtlich das, was der Agent sagen darf)" rows={4}
                        value={editor.antwort}
                        onChange={(e) => setEditor({ ...editor, antwort: e.target.value })}
                        className="w-full text-sm rounded-md border border-border bg-background p-2"
                        data-testid="wb-antwort" />
              <Input placeholder="Tags, kommagetrennt (z. B. kinder, boxen)"
                     value={Array.isArray(editor.tags) ? editor.tags.join(', ') : editor.tags}
                     onChange={(e) => setEditor({ ...editor, tags: e.target.value })} />
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!editor.aktiv}
                       onChange={(e) => setEditor({ ...editor, aktiv: e.target.checked })} />
                Aktiv
              </label>
              <Button onClick={eintragSpeichern} disabled={speichert || !editor.frage.trim() || !editor.antwort.trim()}
                      data-testid="wb-speichern">
                {speichert ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Speichern'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
